import React, { useMemo } from 'react';
import Box from '@mui/material/Box';

import { Flex } from 'src/components/flex';
import Provider from 'src/pages/landlord/conversations/context/Provider';
import Page from 'src/components/page/PageV2';
import Header from 'src/pages/landlord/conversations/components/header/ConversationsHeader';
import KeyListener from 'src/pages/landlord/conversations/components/key-listener';
import NotificationAlert from 'src/pages/landlord/conversations/components/NotificationAlert';
import { SimpleDesktopConversationsTable } from 'src/pages/landlord/conversations/components/list/ConversationsListCategory';
import Toolbar from 'src/pages/landlord/conversations/components/toolbar';
import { useConversationsSearch, isSearchEnabled, type IConversationMatch } from 'src/services/api/messages/use-conversations-search';
import { useProperties } from 'src/services/api/properties/use-properties';
import { type IConversationMatchWithChannel, useConversationsPageContext } from 'src/pages/landlord/conversations/context';
import ConversationsNoSearchResults from 'src/pages/landlord/conversations/components/list/ConversationsNoSearchResults';
import { type ILead } from 'src/services/api';
import { type EMessageChannel } from 'src/services/api';

function NoSearchResultsYet() {
    const text = 'Enter a search query of at least 3 characters or select a filter to see results.';

    return <Box width="100%">
        <ConversationsNoSearchResults text={text} />
    </Box>;
}

/**
 * NOTE: This component should only be rendered if isLoading is false
 */
function PageMainContent() {
    const { search: searchQuery, filter: searchFilter } = useConversationsPageContext();
    const { data: searchResults } = useConversationsSearch(
        searchQuery,
        searchFilter
    );
    const { data: properties } = useProperties();

    const propertiesById = useMemo(() => {
        if (properties === undefined) {
            return undefined;
        }
        return new Map(properties.map((o) => [o.id, o]));
    }, [properties]);

    const leadsByUuid = useMemo(() => {
        if (searchResults === undefined) {
            return undefined;
        }
        const map = new Map<string, ILead>();
        for (const r of searchResults.results) {
            map.set(r.lead.uuid, r.lead);
        }
        return map;
    }, [searchResults]);

    return <Flex width="100%" justifyContent="center" flexWrap="nowrap">
        {
            !isSearchEnabled(searchQuery, searchFilter) &&
            <NoSearchResultsYet />
        }
        {
            (isSearchEnabled(searchQuery, searchFilter) && searchResults !== undefined && propertiesById !== undefined && searchResults.results.length > 0) &&
            <SimpleDesktopConversationsTable
                conversations={searchResults.results.map((r) => {
                    const o: IConversationMatch & {
                        msg_channel?: EMessageChannel;
                    } = Object.assign({}, r.match);
                    o.msg_channel = r.msg_channel;
                    return o as IConversationMatchWithChannel;
                })}
                propertiesById={propertiesById}
                leadsByUuid={leadsByUuid}
            />
        }
        {
            (isSearchEnabled(searchQuery, searchFilter) && searchResults !== undefined && propertiesById !== undefined && searchResults.results.length === 0) &&
            <Box width="100%">
                <ConversationsNoSearchResults text="No conversations match your search." />
            </Box>
        }
    </Flex >;
}

/**
 * This component is necessary because searchQuery/searchFilter come from the provider, and searchQuery and searchFilter are necessary to query for search results
 * This is necessary for isLoading, which is passed to Page
 * It's all a bit convoluted but it works
 */
export function ConversationsSearchPageInner() {
    const { search: searchQuery, filter: searchFilter } = useConversationsPageContext();
    const { isLoading: isConversationsLoading } = useConversationsSearch(
        searchQuery,
        searchFilter
    );
    const { isLoading: isPropertiesLoading } = useProperties();

    const isLoading = isConversationsLoading || isPropertiesLoading;

    return (
        <Page title="Reffie | Search Archive" isLoading={isLoading}>
            <KeyListener />
            <Box flex={1}>
                <NotificationAlert />
                <Header searchScope="all" />

                {/* NOTE: no stages tab on this page */}

                <PageMainContent />
                <Toolbar />

                {/* NOTE: no hotkeys for search results for now */}
            </Box>
        </Page>
    );
}

export default function ConversationsSearchPage() {
    return <Provider>
        <ConversationsSearchPageInner />
    </Provider>;
}
